import {
    BrowserRouter as Router,
    Routes,
    Route,
    Navigate,
    useSearchParams,
} from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { billingPath, isPaidPlanId, peekPendingPlan } from "@/lib/billingPlans";
import LoginPage from "@/pages/LoginPage";
import Dashboard from "@/pages/DashboardPage";
import ProjectsPage from "@/pages/ProjectsPage";
import NewProjectPage from "@/pages/NewProjectPage";
import NewVersionPage from "@/pages/NewVersionPage";
import EditorPage from "@/pages/EditorPage";
import SettingsPage from "@/pages/SettingsPage";
import BillingPage from "@/pages/BillingPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import HelpPage from "@/pages/HelpPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AuthenticatedLayout from "@/components/AuthenticatedLayout";
import TranslationMemoryPage from "./pages/TranslationMemoryPage";
import GlossaryPage from "./pages/GlossaryPage";
import AdminPage from "@/pages/AdminPage";
import { isLinguaproz } from "./config/mode";
import { LinguaprozAuthProvider } from "./context/LinguaprozAuthContext";
import LpLoginPage from "./pages/linguaproz/LoginPage";
import LpDashboardPage from "./pages/linguaproz/DashboardPage";
import EditorPageForLP from "./pages/EditorPage";
import { LyraProvider } from "@/context/LyraContext";
import { ThemeProvider } from "@/context/ThemeContext";
import LyraBubble from "@/components/LyraBubble";

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading...</p>
                </div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    return <AuthenticatedLayout>{children}</AuthenticatedLayout>;
}

// Public Route Component (redirect to dashboard if logged in)
function PublicRoute({ children }: { children: React.ReactNode }) {
    const { user, loading } = useAuth();
    const [searchParams] = useSearchParams();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading...</p>
                </div>
            </div>
        );
    }

    if (user) {
        // A chosen plan (from ?plan= or sessionStorage) takes the user to the
        // billing page instead of the dashboard. It is cleared there.
        const urlPlan = searchParams.get("plan");
        const plan = isPaidPlanId(urlPlan) ? urlPlan : peekPendingPlan();
        return (
            <Navigate to={plan ? billingPath(plan) : "/dashboard"} replace />
        );
    }

    return <>{children}</>;
}

function App() {
    if (isLinguaproz) {
        return (
            <LinguaprozAuthProvider>
                <Router>
                    <Routes>
                        <Route path="/login" element={<LpLoginPage />} />
                        <Route
                            path="/dashboard"
                            element={<LpDashboardPage />}
                        />
                        <Route
                            path="/editor/:projectId"
                            element={
                                <AuthenticatedLayout>
                                    <EditorPageForLP />
                                </AuthenticatedLayout>
                            }
                        />
                        <Route
                            path="*"
                            element={<Navigate to="/dashboard" replace />}
                        />
                    </Routes>
                </Router>
            </LinguaprozAuthProvider>
        );
    }

    return (
        <ThemeProvider>
        <LyraProvider>
        <>
        <Router>
            <Routes>
                {/* Public Routes */}
                <Route
                    path="/login"
                    element={
                        <PublicRoute>
                            <LoginPage />
                        </PublicRoute>
                    }
                />
                <Route
                    path="/signup"
                    element={
                        <PublicRoute>
                            <LoginPage initialMode="signup" />
                        </PublicRoute>
                    }
                />

                {/* Protected Routes */}
                <Route
                    path="/dashboard"
                    element={
                        <ProtectedRoute>
                            <Dashboard />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/projects"
                    element={
                        <ProtectedRoute>
                            <ProjectsPage />
                        </ProtectedRoute>
                    }
                />

                {/* FIXED: /projects/new now routes to NewProjectPage */}
                <Route
                    path="/projects/new"
                    element={
                        <ProtectedRoute>
                            <NewProjectPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/projects/:projectId/new-version"
                    element={
                        <ProtectedRoute>
                            <NewVersionPage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/editor/:projectId"
                    element={
                        <ProtectedRoute>
                            <EditorPage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/projects/:projectId/editor"
                    element={
                        <ProtectedRoute>
                            <EditorPage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/analytics"
                    element={
                        <ProtectedRoute>
                            <AnalyticsPage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/billing"
                    element={
                        <ProtectedRoute>
                            <BillingPage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/settings"
                    element={
                        <ProtectedRoute>
                            <SettingsPage />
                        </ProtectedRoute>
                    }
                />

                {/* Translation Memory - Phase 3 installed, route to dashboard for now */}
                {/* TODO: Create TranslationMemoryPage when ready */}
                <Route
                    path="/translation-memory"
                    element={
                        <ProtectedRoute>
                            <TranslationMemoryPage />
                        </ProtectedRoute>
                    }
                />

                {/* Coming Soon Routes - redirect to dashboard */}
                <Route
                    path="/analytics"
                    element={<Navigate to="/dashboard" replace />}
                />
                <Route
                    path="/glossary"
                    element={
                        <ProtectedRoute>
                            <GlossaryPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/ai-features"
                    element={<Navigate to="/dashboard" replace />}
                />

                {/* Help Route */}
                <Route
                    path="/help"
                    element={
                        <ProtectedRoute>
                            <HelpPage />
                        </ProtectedRoute>
                    }
                />
                {/* Admin Route */}
                <Route
                    path="/admin"
                    element={
                        <ProtectedRoute>
                            <AdminPage />
                        </ProtectedRoute>
                    }
                />

                {/* Default Routes */}
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route
                    path="/"
                    element={<Navigate to="/dashboard" replace />}
                />
                <Route
                    path="*"
                    element={<Navigate to="/dashboard" replace />}
                />
            </Routes>
        <LyraBubble />
        </Router>
        </>
        </LyraProvider>
        </ThemeProvider>
    );
}

export default App;
